export const LEGACY_WORKSPACE_CHART_LAB_PATH = '/__demo/position-workspace-chart'
export const POSITION_WORKSPACE_FALLBACK_PATH = '/positions/workspace'
export const POSITION_WORKSPACE_SELECTION_STORAGE_KEY = 'erevis_position_workspace_selection'

function clean(value) {
  return String(value || '').trim()
}

function safeDecode(value) {
  try {
    return decodeURIComponent(String(value || ''))
  } catch {
    return ''
  }
}

function normalizedSymbol(value) {
  return clean(value).toUpperCase()
}

function normalizedProjectIds(projectIds, projectId) {
  const values = Array.isArray(projectIds) ? projectIds : []
  const normalized = values.map(clean).filter(Boolean)
  if (normalized.length > 0) return [...new Set(normalized)]
  const fallback = clean(projectId)
  return fallback ? [fallback] : []
}

function target(pathname, params) {
  const search = params.toString()
  return { pathname, search: search ? `?${search}` : '' }
}

export function isCanonicalPositionWorkspacePath(pathname) {
  return String(pathname || '') === POSITION_WORKSPACE_FALLBACK_PATH
}

export function isPositionWorkspacePath(pathname) {
  const value = String(pathname || '')
  return isCanonicalPositionWorkspacePath(value)
    || /^\/positions\/(?:board|symbol)\/[^/]+$/.test(value)
}

export function readPositionWorkspaceSelection() {
  try {
    const raw = localStorage.getItem(POSITION_WORKSPACE_SELECTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const symbol = normalizedSymbol(parsed?.symbol)
    const projectIds = normalizedProjectIds(parsed?.projectIds)
    const timeframe = clean(parsed?.timeframe) || null
    return symbol ? { symbol, projectIds, timeframe } : null
  } catch {
    return null
  }
}

export function rememberPositionWorkspaceSelection({ symbol, projectIds = [], timeframe } = {}) {
  const normalized = normalizedSymbol(symbol)
  if (!normalized) return
  const selection = {
    symbol: normalized,
    projectIds: normalizedProjectIds(projectIds),
    timeframe: clean(timeframe) || null,
  }
  try {
    localStorage.setItem(POSITION_WORKSPACE_SELECTION_STORAGE_KEY, JSON.stringify(selection))
  } catch {
    // The workspace remains usable when browser storage is unavailable.
  }
}

export function buildPositionWorkspaceTarget({
  symbol,
  projectId,
  projectIds,
  timeframe,
} = {}) {
  const ids = normalizedProjectIds(projectIds, projectId)
  const params = new URLSearchParams()

  const nextSymbol = normalizedSymbol(symbol)
  if (nextSymbol) {
    params.set('symbol', nextSymbol)
  }
  if (ids.length > 1) {
    params.set('ids', ids.join(','))
  } else if (ids.length === 1) {
    params.set('projectId', ids[0])
  }
  if (clean(timeframe)) params.set('timeframe', clean(timeframe))

  return target(POSITION_WORKSPACE_FALLBACK_PATH, params)
}

export function canonicalizePositionWorkspaceRoute(page) {
  let parsed
  try {
    parsed = new URL(page || '', 'https://erevis.local')
  } catch {
    return { pathname: POSITION_WORKSPACE_FALLBACK_PATH, search: '' }
  }

  if (parsed.pathname === POSITION_WORKSPACE_FALLBACK_PATH) {
    return { pathname: parsed.pathname, search: parsed.search }
  }

  const legacySymbolMatch = parsed.pathname.match(/^\/positions\/symbol\/([^/]+)$/)
  if (legacySymbolMatch) {
    return buildPositionWorkspaceTarget({
      symbol: safeDecode(legacySymbolMatch[1]),
      timeframe: parsed.searchParams.get('timeframe'),
    })
  }

  const legacyProjectMatch = parsed.pathname.match(/^\/positions\/board\/([^/]+)$/)
  if (legacyProjectMatch) {
    return buildPositionWorkspaceTarget({
      projectId: safeDecode(legacyProjectMatch[1]),
      projectIds: parsed.searchParams.get('ids')?.split(',') || [],
      timeframe: parsed.searchParams.get('timeframe'),
    })
  }

  if (parsed.pathname !== LEGACY_WORKSPACE_CHART_LAB_PATH) {
    return { pathname: parsed.pathname, search: parsed.search }
  }

  const ids = parsed.searchParams.get('ids')
  return buildPositionWorkspaceTarget({
    symbol: parsed.searchParams.get('symbol'),
    projectId: parsed.searchParams.get('projectId'),
    projectIds: ids ? ids.split(',') : [],
    timeframe: parsed.searchParams.get('timeframe'),
  })
}

export function canonicalizeWorkspaceRouteValue(value, fallbackSymbol) {
  if (value) {
    const canonical = canonicalizePositionWorkspaceRoute(value)
    if (canonical.pathname === POSITION_WORKSPACE_FALLBACK_PATH) {
      const params = new URLSearchParams(canonical.search)
      if (params.has('symbol') || params.has('projectId') || params.has('ids') || !fallbackSymbol) {
        return `${canonical.pathname}${canonical.search}`
      }
    } else if (!fallbackSymbol) {
      return `${canonical.pathname}${canonical.search}`
    }
  }
  const fallback = buildPositionWorkspaceTarget({ symbol: fallbackSymbol })
  return `${fallback.pathname}${fallback.search}`
}
